import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { announcementsApi } from '@/services/announcementsApi';
import {
  GetAnnouncementsRequest,
  GetPublicAnnouncementsRequest,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  AddInteractionRequest,
  AnnouncementWithInteractions
} from '@/types/announcements';

// Query Keys - consistent naming for cache management
export const announcementKeys = {
  all: ['announcements'] as const,
  lists: () => [...announcementKeys.all, 'list'] as const,
  list: (params: GetAnnouncementsRequest) => [...announcementKeys.lists(), params] as const,
  publicLists: () => [...announcementKeys.all, 'public'] as const,
  publicList: (params: GetPublicAnnouncementsRequest) => [...announcementKeys.publicLists(), params] as const,
  details: () => [...announcementKeys.all, 'detail'] as const,
  detail: (id: string) => [...announcementKeys.details(), id] as const,
} as const;

/**
 * Hook to fetch announcements for authenticated users
 * Matches backend GET /announcements endpoint exactly
 */
export function useAnnouncements(params: GetAnnouncementsRequest = {}) {
  return useQuery({
    queryKey: announcementKeys.list(params),
    queryFn: () => announcementsApi.getAnnouncements(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });
}

/**
 * Hook to fetch public announcements (no authentication required)
 * Matches backend GET /announcements/public endpoint exactly
 */
export function usePublicAnnouncements(params: GetPublicAnnouncementsRequest = {}) {
  return useQuery({
    queryKey: announcementKeys.publicList(params),
    queryFn: () => announcementsApi.getPublicAnnouncements(params),
    staleTime: 1000 * 60 * 10, // 10 minutes (public data changes less frequently)
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch a specific announcement by ID
 * Matches backend GET /announcements/{id} endpoint exactly
 */
export function useAnnouncement(id: string) {
  return useQuery({
    queryKey: announcementKeys.detail(id),
    queryFn: () => announcementsApi.getAnnouncement(id),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    enabled: !!id, // Only run if ID is provided
  });
}

/**
 * Hook for announcement mutations (create, update, publish, delete)
 * Handles cache invalidation automatically
 */
export function useAnnouncementMutations() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateAnnouncementRequest) => announcementsApi.createAnnouncement(data),
    onSuccess: () => {
      // Invalidate all announcement lists to refresh data
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: announcementKeys.publicLists() });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementRequest }) =>
      announcementsApi.updateAnnouncement(id, data),
    onSuccess: (data, variables) => {
      // Update the specific announcement in cache
      queryClient.setQueryData(announcementKeys.detail(variables.id), data);
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: announcementKeys.publicLists() });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => announcementsApi.publishAnnouncement(id),
    onSuccess: (data, id) => {
      // Update the specific announcement in cache
      queryClient.setQueryData(announcementKeys.detail(id), data);
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: announcementKeys.publicLists() });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => announcementsApi.deleteAnnouncement(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: announcementKeys.detail(id) });
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: announcementKeys.publicLists() });
    },
  });

  return {
    createAnnouncement: createMutation,
    updateAnnouncement: updateMutation,
    publishAnnouncement: publishMutation,
    deleteAnnouncement: deleteMutation,
  };
}

/**
 * Hook for announcement interactions (like, love, RSVP, etc.)
 * Handles optimistic updates for better UX
 */
export function useAnnouncementInteractions() {
  const queryClient = useQueryClient();

  const addInteractionMutation = useMutation({
    mutationFn: ({ id, interaction }: { id: string; interaction: AddInteractionRequest }) =>
      announcementsApi.addInteraction(id, interaction),
    onMutate: async ({ id, interaction }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: announcementKeys.detail(id) });

      // Snapshot the previous value
      const previousAnnouncement = queryClient.getQueryData<AnnouncementWithInteractions>(
        announcementKeys.detail(id)
      );

      // Optimistically update the cache
      if (previousAnnouncement) {
        const updatedAnnouncement: AnnouncementWithInteractions = {
          ...previousAnnouncement,
          interactions: {
            ...previousAnnouncement.interactions,
            // Optimistically increment the interaction count
            [interaction.interactionType === 'rsvp_yes' ? 'rsvpYes' :
             interaction.interactionType === 'rsvp_no' ? 'rsvpNo' :
             interaction.interactionType + 's']: 
              previousAnnouncement.interactions[
                interaction.interactionType === 'rsvp_yes' ? 'rsvpYes' :
                interaction.interactionType === 'rsvp_no' ? 'rsvpNo' :
                (interaction.interactionType + 's') as keyof typeof previousAnnouncement.interactions
              ] + 1
          },
          userInteractions: [
            ...(previousAnnouncement.userInteractions || []),
            interaction.interactionType
          ]
        };

        queryClient.setQueryData(announcementKeys.detail(id), updatedAnnouncement);
      }

      return { previousAnnouncement };
    },
    onError: (_, { id }, context) => {
      // Revert optimistic update on error
      if (context?.previousAnnouncement) {
        queryClient.setQueryData(announcementKeys.detail(id), context.previousAnnouncement);
      }
    },
    onSuccess: (data, { id }) => {
      // Update with actual server response
      queryClient.setQueryData(announcementKeys.detail(id), data.announcement);
      
      // Invalidate lists to refresh interaction counts
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() });
    },
  });

  const removeInteractionMutation = useMutation({
    mutationFn: ({ id, interactionType }: { id: string; interactionType: string }) =>
      announcementsApi.removeInteraction(id, interactionType),
    onSuccess: (_, { id }) => {
      // Invalidate the specific announcement and lists
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() });
    },
  });

  return {
    addInteraction: addInteractionMutation,
    removeInteraction: removeInteractionMutation,
  };
}

/**
 * Prefetch announcements for better performance
 */
export function usePrefetchAnnouncements() {
  const queryClient = useQueryClient();

  const prefetchAnnouncements = (params: GetAnnouncementsRequest = {}) => {
    queryClient.prefetchQuery({
      queryKey: announcementKeys.list(params),
      queryFn: () => announcementsApi.getAnnouncements(params),
      staleTime: 1000 * 60 * 5,
    });
  };

  const prefetchPublicAnnouncements = (params: GetPublicAnnouncementsRequest = {}) => {
    queryClient.prefetchQuery({
      queryKey: announcementKeys.publicList(params),
      queryFn: () => announcementsApi.getPublicAnnouncements(params),
      staleTime: 1000 * 60 * 10,
    });
  };

  const prefetchAnnouncement = (id: string) => {
    queryClient.prefetchQuery({
      queryKey: announcementKeys.detail(id),
      queryFn: () => announcementsApi.getAnnouncement(id),
      staleTime: 1000 * 60 * 5,
    });
  };

  return {
    prefetchAnnouncements,
    prefetchPublicAnnouncements,
    prefetchAnnouncement,
  };
}